import React, { useState, useRef, useEffect } from 'react';
import { format, setYear, setMonth } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import './CalendarHeader.scss';

const MONTHS = [
  ['Jan', 'Feb', 'Mar'],
  ['Apr', 'May', 'Jun'],
  ['Jul', 'Aug', 'Sep'],
  ['Oct', 'Nov', 'Dec']
];

const MONTH_MAP = {
  'Jan': 0, 'Feb': 1, 'Mar': 2,
  'Apr': 3, 'May': 4, 'Jun': 5,
  'Jul': 6, 'Aug': 7, 'Sep': 8,
  'Oct': 9, 'Nov': 10, 'Dec': 11
};

const generateYearRange = (currentYear) => {
  const years = [];
  for (let i = currentYear - 4; i <= currentYear + 4; i++) {
    years.push(i);
  }
  return years;
};

export function CalendarHeader({ date, onPrevMonth, onNextMonth }) {
  const [dropdownState, setDropdownState] = useState('closed');
  const dropdownRef = useRef(null);
  const currentMonth = format(date, 'MMM');
  const currentYear = date.getFullYear();

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownState('closed');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMonthSelect = (month) => {
    const monthIndex = MONTH_MAP[month];
    const newDate = new Date(date.getFullYear(), monthIndex, 1);

    if (monthIndex > date.getMonth()) {
      onNextMonth(newDate);
    } else if (monthIndex < date.getMonth()) {
      onPrevMonth(newDate);
    }

    setDropdownState('closed');
  };

  const handleHeaderClick = () => {
    setDropdownState(prev => {
      if (prev === 'closed') return 'year';
      if (prev === 'year') return 'years';
      return 'closed';
    });
  };

  const handleYearSelect = (year) => {
    const newDate = new Date(year, date.getMonth(), 1);

    if (year > currentYear) {
      onNextMonth(newDate);
    } else if (year < currentYear) {
      onPrevMonth(newDate);
    }

    setDropdownState('year');
  };

  const handlePrevMonth = () => {
    const newDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    onPrevMonth(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    onNextMonth(newDate);
  };

  return (
    <div className="calendar-header xl:mb-2 mb-2 2xl:mb-2 3xl:mb-4">
      <button
        onClick={handlePrevMonth}
        className="nav-button"
        aria-label="Previous month"
      >
        <ChevronLeft />
      </button>

      <div className="relative inline-block" ref={dropdownRef}>
        <button
          onClick={handleHeaderClick}
          className="month-year-button"
          aria-haspopup="true"
          aria-expanded={dropdownState !== 'closed'}
        >
          <span className="month-year-text">
            {dropdownState === 'years' ? currentYear : format(date, 'MMMM yyyy')}
          </span>
          <ChevronDown />
        </button>

        {dropdownState !== 'closed' && (
          <div className="dropdown-menu" role="dialog" aria-label={dropdownState === 'years' ? "Year selection" : "Month selection"}>
            {dropdownState === 'year' ? (
              <div className="grid-layout">
                {MONTHS.map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    {row.map((month) => (
                      <button
                        key={month}
                        onClick={() => handleMonthSelect(month)}
                        className={`${month === currentMonth ? 'selected' : ''}`}
                      >
                        {month}
                      </button>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="grid-layout">
                {generateYearRange(currentYear).map((year) => (
                  <button
                    key={year}
                    onClick={() => handleYearSelect(year)}
                    className={`${year === currentYear ? 'selected' : ''}`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <button
        onClick={handleNextMonth}
        className="nav-button"
        aria-label="Next month"
      >
        <ChevronRight />
      </button>
    </div>
  );
}